import { type Clinic } from '../clinic/clinic.model'
import { INSURANCE_PROVIDER_LOOKUP_TYPE } from '../insurance-provider-lookup/insurance-provider-lookup-type'
import { isLikeSelfPayInsuranceType } from '../insurance-provider-lookup/is-like-self-pay'
import { PatientInsurance, PatientInsuranceStatus, PatientInsuranceType } from './patient-insurance.model'

/**
 * Whether `insurance` should be treated as self-pay-like (no eligibility pull, "Not required"
 * status). Prefers the api-computed, persisted `isSelfPay` flag (set on every create/update via
 * `isSelfPayInsurance()`, backfilled onto older rows by the `update-patient-insurance-self-pay`
 * task) and falls back to the raw type check for any record that flag hasn't reached yet.
 */
export function isSelfPayLikeInsurance(insurance: PatientInsurance | undefined): boolean {
  return !!insurance?.isSelfPay || isLikeSelfPayInsuranceType(insurance?.lookupType ?? insurance?.lookup?.type)
}

/**
 * Medical coverages of `type` — the candidate set for the picker and the save payload. Shares only
 * the backend `pickInsurance` status precedence for primary: Active coverages, else Not Verified ones.
 * Secondary stays Active-only: a Not Verified one would turn has secondary on for new estimates
 * Other slots also require Active coverage. Unlike
 * `pickInsurance`, it never falls back to Inactive or other statuses and returns every candidate
 * rather than one. `getType` lets callers filter on a per-account resolved role instead of the raw
 * patient-level `type`.
 */
export function selectableInsurances(
  insurances: PatientInsurance[],
  type: PatientInsuranceType,
  getType: (insurance: PatientInsurance) => PatientInsuranceType | undefined = insurance => insurance.type,
): PatientInsurance[] {
  const ofType = insurances.filter(
    i => getType(i) === type && i.lookupType === INSURANCE_PROVIDER_LOOKUP_TYPE.MEDICAL_INSURANCE,
  )
  const active = ofType.filter(i => i.status === PatientInsuranceStatus.Active)
  return active.length > 0 || type !== PatientInsuranceType.PRIMARY
    ? active
    : ofType.filter(i => i.status === PatientInsuranceStatus.NotVerified)
}

/**
 * Picks an insurance for an estimator slot in this order:
 *   1. `current` — operator's pick, kept if still selectable so a coverage refresh mid-edit can't clobber it.
 *   2. `preferredId` — the id captured on the estimate, so reopening a save re-selects the original choice.
 *   3. The lone option when only one exists; otherwise `null` to force an explicit choice.
 */
export function resolveSelectedInsurance(
  options: PatientInsurance[],
  current: PatientInsurance | null | undefined,
  preferredId: string | null | undefined,
): PatientInsurance | null {
  if (options.length === 0) {
    return null
  }
  if (current) {
    const stillSelectable = options.find(i => i.id === current.id)
    if (stillSelectable) {
      return stillSelectable
    }
  }
  if (preferredId) {
    const preferred = options.find(i => i.id === preferredId)
    if (preferred) {
      return preferred
    }
  }
  if (options.length === 1) {
    return options[0]
  }
  return null
}

/**
 * Whether `clinic` resolves each policy's coverage role per visit/account rather than
 * one shared role for the whole patient (see PatientInsurance.accountTypes).
 */
export function isPerVisitInsuranceSelectionEnabled(clinic: Clinic | undefined): boolean {
  return !!clinic?.modulesPreferences?.perVisitInsuranceSelection
}

/**
 * Whether `insurance` is in scope for `accountId` — true when the policy is unscoped
 * (accountIds empty/absent, so it applies to every visit) or accountIds includes this
 * account. When per-visit selection isn't enabled for this clinic (or there's no account
 * context), every policy is in scope patient-wide.
 */
export function isInsuranceRelevantToVisit(
  insurance: PatientInsurance,
  clinic: Clinic | undefined,
  accountId: string | null | undefined,
): boolean {
  if (!isPerVisitInsuranceSelectionEnabled(clinic) || !accountId) {
    return true
  }
  return !insurance.accountIds?.length || insurance.accountIds.includes(accountId)
}

/**
 * Same relevance rule as isInsuranceRelevantToVisit, generalized to a set of accounts —
 * for callers that may have more than one account in scope at once (e.g. a combined
 * multi-visit estimate). Callers own their own per-visit-flag gate; pass every account
 * that's genuinely relevant, or `null` to mean "not scoped" (matches everything).
 */
export function isInsuranceRelevantToAnyAccount(
  insurance: PatientInsurance,
  accountIds: ReadonlySet<string> | null,
): boolean {
  if (!accountIds) {
    return true
  }
  return !insurance.accountIds?.length || insurance.accountIds.some(id => accountIds.has(id))
}

/**
 * Picks the insurance of `type` most relevant to `accountId`. getAccountType() alone can't
 * disambiguate two accountIds-scoped records sharing the same raw `type` with no accountTypes
 * map between them, so this scopes by accountIds first, then resolves role, and prefers an
 * account-scoped match over an unscoped fallback. Mirrors appointment-eligibility-card's
 * getPatientInsurance().
 *
 * `alwaysScopeToAccount` keeps the accountIds preference active even when per-visit role
 * resolution is off — needed by callers (appointment-eligibility-card) whose account
 * disambiguation predates and isn't gated by that flag. Defaults to false so other callers
 * (authorization-dialog, pull-prior-authorization) keep their existing flag-gated behavior.
 */
export function findInsuranceForAccount(
  insurances: PatientInsurance[],
  type: PatientInsuranceType,
  clinic: Clinic | undefined,
  accountId: string | null | undefined,
  { alwaysScopeToAccount = false }: { alwaysScopeToAccount?: boolean } = {},
): PatientInsurance | undefined {
  const perVisitEnabled = isPerVisitInsuranceSelectionEnabled(clinic)
  const candidates = insurances
    .filter(insurance => isInsuranceRelevantToVisit(insurance, clinic, accountId))
    .filter(insurance => insurance.getAccountType(accountId, perVisitEnabled) === type)

  if (!accountId || (!perVisitEnabled && !alwaysScopeToAccount)) {
    return candidates[0]
  }

  return (
    candidates.find(insurance => insurance.accountIds?.includes(accountId)) ??
    candidates.find(insurance => !insurance.accountIds?.length)
  )
}
